import { Link } from 'react-router-dom';

import { useAuthStore } from '../auth/store.js';
import { HomeHeader } from '../components/HomeHeader.js';
import { AdminHomeScreen } from './admin/AdminHomeScreen.js';

/**
 * Личный кабинет. У каждой роли он свой.
 *
 * Раньше экран был общий: HR и руководитель видели «Сдать смену» и «Не выйду»,
 * хотя ни того, ни другого не делают, а оператор — блоки проверки связи,
 * оставшиеся с ранних этапов. Теперь каждый видит только свою работу.
 */

function tile(to: string, label: string): JSX.Element {
  return (
    <Link
      key={to}
      to={to}
      className="rounded-xl bg-card shadow-card px-4 py-4 text-center text-lg font-semibold active:bg-line"
    >
      {label}
    </Link>
  );
}

/** Оператор: сдать смену и всё, что касается его собственного графика. */
function OperatorHome(): JSX.Element {
  return (
    <div className="min-h-screen px-4 py-6">
      {/* Заголовок называет экран, а не то, что на нём главное: кроме сдачи
          смены здесь и отчёты, и график, и выходные. Кто вошёл и в какой
          роли — написано строкой ниже, повторять это заголовком незачем. */}
      <HomeHeader title="Личный кабинет" />

      <section className="mb-4 flex flex-col gap-touch">
        <Link
          to="/report"
          className="rounded-xl bg-emerald-600 px-5 py-6 text-center text-xl font-semibold text-white active:bg-emerald-700"
        >
          Сдать смену
        </Link>
        {/* «Не выйду» здесь больше нет: кнопка на видном месте превращает
            невыход в один касание, и пользоваться ею начинают чаще, чем
            следует. Невыход теперь сообщают мастеру, а он его записывает. */}
        <div className="grid grid-cols-2 gap-touch">
          {tile('/my-reports', 'Мои отчёты')}
          {tile('/my-schedule', 'Мой график')}
          {tile('/wishes', 'Выходные')}
        </div>
        <Link
          to="/queue"
          className="rounded-xl bg-muted px-5 py-4 text-center text-base font-semibold active:bg-line"
        >
          Что ещё не ушло на сервер
        </Link>
      </section>
    </div>
  );
}

/**
 * HR: работа с кандидатами идёт в отдельном приложении — там персональные
 * данные людей, которые в компании не работают, и отдельный доступ.
 */
function HrHome(): JSX.Element {
  return (
    <div className="min-h-screen px-4 py-6">
      <HomeHeader title="Личный кабинет" />

      <section className="flex flex-col gap-touch">
        {/* Отдельное приложение, поэтому обычная ссылка, а не маршрут роутера */}
        <a
          href="/hr/"
          target="_blank"
          rel="noopener"
          className="rounded-xl bg-emerald-600 px-5 py-6 text-center text-xl font-semibold text-white active:bg-emerald-700"
        >
          Кандидаты и собеседования
        </a>
        <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">
          Воронка, звонки, записи на экскурсию и стажировку, реестр неявок —
          всё в приложении найма.
        </p>
      </section>
    </div>
  );
}

/** Руководитель: сводка по производству и то, что задаёт правила работы. */
function ManagerHome(): JSX.Element {
  return (
    <div className="min-h-screen px-4 py-6">
      <HomeHeader title="Личный кабинет" />

      <section className="flex flex-col gap-touch">
        <Link
          to="/dashboard"
          className="rounded-xl bg-emerald-600 px-5 py-6 text-center text-xl font-semibold text-white active:bg-emerald-700"
        >
          Сводка и выгрузки
        </Link>
        <div className="grid grid-cols-2 gap-touch">
          {tile('/schedule', 'График смен')}
          {tile('/admin/employees', 'Сотрудники')}
          {tile('/stock', 'Остатки сырья')}
          {tile('/recipes', 'Рецептуры')}
          {tile('/norms', 'Нормативы')}
          {tile('/receipts', 'Приёмка сырья')}
          {tile('/shipments', 'Отгрузка')}
        </div>
      </section>
    </div>
  );
}

/**
 * Мастер участка: принимает смены и ведёт график своего участка.
 *
 * Сдавать отчёты ему нечего — на агрегатах работают операторы. Поэтому
 * «Сдать смену» и «Не выйду» здесь не показываются: чужие кнопки на экране
 * человека, который ими не пользуется, только мешают.
 */
function MasterHome(): JSX.Element {
  return (
    <div className="min-h-screen px-4 py-6">
      <HomeHeader title="Личный кабинет" />

      <section className="flex flex-col gap-touch">
        <Link
          to="/master/acceptance"
          className="rounded-xl bg-emerald-600 px-5 py-6 text-center text-xl font-semibold text-white active:bg-emerald-700"
        >
          Принять смены
        </Link>
        {/* Невыход записывает мастер: у самого работника такой кнопки нет,
            иначе ею пользуются чаще, чем следует. Без этого пути данные
            о срывах смен просто перестали бы попадать в систему. */}
        <div className="grid grid-cols-2 gap-touch">
          {tile('/schedule', 'График смен')}
          {tile('/absence', 'Записать невыход')}
          {tile('/admin/missing', 'Несданные смены')}
          {tile('/admin/reports', 'Отчёты участка')}
          {tile('/receipts', 'Приёмка сырья')}
          {tile('/dashboard', 'Сводка')}
        </div>
      </section>
    </div>
  );
}

export function HomeScreen(): JSX.Element {
  const role = useAuthStore((state) => state.user?.role);

  if (role === 'ADMIN') return <AdminHomeScreen />;
  if (role === 'HR') return <HrHome />;
  if (role === 'MANAGER') return <ManagerHome />;
  if (role === 'MASTER') return <MasterHome />;
  return <OperatorHome />;
}
